import React, { useContext } from "react";
import { DoubleColumnWrapper, SecondColumn, SingleProductContentWrapper } from "./Styled";
import { ProductOrder } from "../../../data/entities";
import ProductList from "./ProductList";
import CustomerInfo from "./CustomerInfo";
import DeliveryAddress from "./DeliveryAddress";
import Priority from "./Priority";
import Note from "./Note";
import Summary from "./Summary";
import { Assignees } from "./Assignees";
import apiMethods from "../../../utils/api";
import { PRODUCT_ORDER_STATUS, ROLE, SALE_ORDER_TYPE } from "../../../data/enums";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import { MaterialOrderLinkedWrapper, MaterialTableText } from "../detail/Styled";
import { MaterialOrderLink } from "./MaterialOrderLink";
import { ContentWrapper } from "../../../shared/styled/TableStyles";
import PurchaseOrderNumber from "../../SaleOrder/single/PurchaseOrderNumber";

interface SingleProductOrderProps {
    productOrder: ProductOrder;
    mergeProductOrder: Function;
    fetchProductOrder?: any;
    isView?: boolean;
    hasCreatedMaterialOrderForStorage?: (storage: "storage_1" | "storage_2") => string;
    materialOrderIdFromStorage1?: string;
    materialOrderIdFromStorage2?: string;
    materialOrderNameFromStorage1?: string;
    materialOrderNameFromStorage2?: string;
    poNumberError?: string;
}

interface AssigneeField {
    title: string; // The display title for the assignee
    fieldName: string; // The field name used for the assignee
    role: string; // The role of the assignee
}

const SingleProductOrder = ({
                                productOrder,
                                mergeProductOrder,
                                fetchProductOrder,
                                isView = false,
                                hasCreatedMaterialOrderForStorage = () => "",
                                materialOrderIdFromStorage1 = "",
                                materialOrderIdFromStorage2 = "",
                                materialOrderNameFromStorage1 = "",
                                materialOrderNameFromStorage2 = "",
                                poNumberError,
                            }: SingleProductOrderProps) => {
    const updateProductOrder = async (data: any) => {
        await apiMethods.patch(`/products/orders/${productOrder?.id}/`, data);
    };

    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const canSeeSummary = ![ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role);

    const assigneeCases = (): AssigneeField[] => {
        const requiredStorageKeepers = productOrder.required_storage_keepers || [];

        const storageKeeperAssignments = requiredStorageKeepers
            .map((keeper) => {
                if (keeper === "first_storage_keeper") {
                    return {
                        title: "Thủ kho 1",
                        fieldName: "first_storage_keeper",
                        role: "first_storage_keeper",
                    };
                } else if (keeper === "second_storage_keeper") {
                    return {
                        title: "Thủ kho 2",
                        fieldName: "second_storage_keeper",
                        role: "second_storage_keeper",
                    };
                }
                return null; // Return null for unsupported roles
            })
            .filter((assignment): assignment is AssigneeField => assignment !== null); // Filter out null values

        switch (productOrder.status) {
            case PRODUCT_ORDER_STATUS.BUYING_MATERIAL:
            case PRODUCT_ORDER_STATUS.IN_PRODUCTION:
            case PRODUCT_ORDER_STATUS.PROCESSING:
                if (!productOrder.has_sufficient_materials) {
                    return [
                        {
                            title: "Nhân viên mua vật tư",
                            fieldName: "purchasing_staff",
                            role: "purchasing_staff",
                        },
                        ...storageKeeperAssignments,
                    ];
                } else if (!productOrder.has_sufficient_products) {
                    return [...storageKeeperAssignments];
                } else if (productOrder.has_sufficient_products && productOrder.has_sufficient_materials) {
                    return [...storageKeeperAssignments];
                }
                return [];
            case PRODUCT_ORDER_STATUS.ASSIGNING_STORAGE_KEEPER:
                return [...storageKeeperAssignments];
            case PRODUCT_ORDER_STATUS.PENDING:
            case PRODUCT_ORDER_STATUS.DECLINED:
            case PRODUCT_ORDER_STATUS.IMPORTING_TO_STORAGE:
            case PRODUCT_ORDER_STATUS.DELIVERING:
            case PRODUCT_ORDER_STATUS.COMPLETED:
                if (productOrder.has_sufficient_products) {
                    return [];
                }
                if (!productOrder.has_sufficient_materials) {
                    return [
                        {
                            title: "Nhân viên mua vật tư",
                            fieldName: "purchasing_staff",
                            role: "purchasing_staff",
                        },
                        ...storageKeeperAssignments,
                    ];
                }
                return [...storageKeeperAssignments];
            default:
                return [];
        }
    };

    const defaultAssignees = () => {
        const defaultAssignees = [
            {
                title: "Nhân viên Sale",
                fieldName: "sale_staff",
                role: "sale_staff",
            },
            {
                title: "Nhân viên hậu cần điều phối",
                fieldName: "production_staff",
                role: "production_staff",
            },
        ];
        return defaultAssignees;
    };

    return (
        <SingleProductContentWrapper>
            {(hasCreatedMaterialOrderForStorage("storage_1") || hasCreatedMaterialOrderForStorage("storage_2")) && (
                <>
                    <ContentWrapper>
                        <MaterialTableText>Thông tin đơn hàng sản phẩm</MaterialTableText>

                        <MaterialOrderLinkedWrapper>
                            {hasCreatedMaterialOrderForStorage("storage_1") && (
                                <MaterialOrderLink materialOrderId={materialOrderIdFromStorage1} storage="Kho 1"
                                                   name={materialOrderNameFromStorage1} />
                            )}
                            {hasCreatedMaterialOrderForStorage("storage_2") && (
                                <MaterialOrderLink materialOrderId={materialOrderIdFromStorage2} storage="Kho 2"
                                                   name={materialOrderNameFromStorage2} />
                            )}
                        </MaterialOrderLinkedWrapper>
                    </ContentWrapper>
                </>
            )}
            {productOrder.order_type !== SALE_ORDER_TYPE.RESERVE && (
                <DeliveryAddress
                    key={productOrder.customer?.id}
                    customer={productOrder.customer}
                    value={productOrder.delivery_address || ""}
                    onChange={(delivery_address) => mergeProductOrder({ delivery_address })}
                    isView={isView}
                />
            )}
            <ProductList
                productOrder={productOrder}
                mergeProductOrder={mergeProductOrder}
                isView={isView}
                orderState={productOrder.status}
            />
            <DoubleColumnWrapper>
                <SecondColumn>
                    <CustomerInfo
                        customer={productOrder.customer}
                    />
                    {canSeeSummary && (
                        <Summary
                            data={productOrder.products}
                            materials={productOrder.materials}
                            orderState={productOrder.status}
                        />
                    )}
                </SecondColumn>
                <SecondColumn>
                    <PurchaseOrderNumber
                        value={productOrder.po_number}
                        setValue={(value) => mergeProductOrder({ po_number: value })}
                        isView={isView || Boolean(productOrder.id && productOrder.status !== PRODUCT_ORDER_STATUS.OPEN)}
                    />
                    {!isView && (
                        <Priority
                            productOrder={productOrder}
                            mergeProductOrder={mergeProductOrder}
                        />
                    )}

                    {isView && (
                        <Assignees
                            data={productOrder}
                            updateData={updateProductOrder}
                            fetchData={fetchProductOrder}
                            fields={[...defaultAssignees(), ...assigneeCases()]}
                        />
                    )}
                    <Note
                        text={productOrder.note}
                        setText={(value: string) =>
                            mergeProductOrder({ note: value })
                        }
                        isView={isView}
                    />
                </SecondColumn>
            </DoubleColumnWrapper>
        </SingleProductContentWrapper>
    );
};

export default SingleProductOrder;
